import {DateTime} from "luxon";
import {TimeTool} from "../lib/TimeTool.js";
import {DailyTimesheetReport} from "./DailyTimesheetReport.js";
import {NO_CUSTOMER_ID} from "./TimesheetEntry.js";
import {WeeklyTimesheetReport} from "./WeeklyTimesheetReport.js";
import {Customer} from "../crm/Customer.js";

export class TimesheetReportTool {
    /**
     *
     * @param {Date} date
     * @param {TimesheetEntry[]} entries
     * @param {PlannedTime[]} plannedTimes
     * @return {DailyTimesheetReport}
     */
    createTodayReport(date, entries, plannedTimes)
    {
        const report = new DailyTimesheetReport();
        const dateString = TimeTool.dayAsString(date);

        report.date = date;

        let startOfWeek = DateTime.fromJSDate(date).setZone('Europe/Berlin').startOf('week').toJSDate();
        let startOfToday = DateTime.fromJSDate(date).setZone('Europe/Berlin').startOf('day').toJSDate();
        let startOfMonth = DateTime.fromJSDate(date).setZone('Europe/Berlin').startOf('month').toJSDate();

        for (let entry of entries) {

            if (entry.start.getTime() >= startOfWeek.getTime() && entry.start.getTime() < startOfToday.getTime() ) {
                report.previousEntriesFromThisWeek.push(entry);
            }

            if (entry.start.getTime() >= startOfMonth.getTime() && entry.start.getTime() < startOfToday.getTime() ) {
                report.previousEntriesFromThisMonth.push(entry);
            }

            if (TimeTool.dayAsString(entry.start) !== dateString) {
                continue;
            }

            if (!entry.isLogged) {
                report.runningEntry = entry;
                continue; // Do not add the current one, or it will be count double
            }

            report.todayEntries.push(entry);

        }

        for (let plannedTime of plannedTimes) {
            if (TimeTool.dayAsString(plannedTime.start) === dateString) {
                report.todayPlannedEntries.push(plannedTime);
            }
            if (plannedTime.start.getTime() > startOfWeek.getTime() && plannedTime.start.getTime() < startOfToday.getTime() ) {
                report.previousPlannedTimesFromThisWeek.push(plannedTime);
            }
            if (plannedTime.start.getTime() > startOfMonth.getTime() && plannedTime.start.getTime() < startOfToday.getTime() ) {
                report.previousPlannedTimesFromThisMonth.push(plannedTime);
            }
        }

        this._addDailyStats(report, entries, plannedTimes);

        return report;
    }

    /**
     * @param {Date} dayInWeek
     * @param {TimesheetEntry[]} entries
     * @param {PlannedTime[]} plannedTimes
     * @return {WeeklyTimesheetReport}
     */
    createWeeklyReport(dayInWeek, entries, plannedTimes) {

        const report = new WeeklyTimesheetReport();

        const luxonStartOfWeek = DateTime.fromJSDate(dayInWeek).setZone('Europe/Berlin').startOf('week');
        report.startOfWeek = luxonStartOfWeek.toJSDate();
        report.endOfWeek = DateTime.fromJSDate(dayInWeek).setZone('Europe/Berlin').endOf('week').toJSDate();

        /** @type {Map<number, {weekDay:string,billable:number,notBillable:number,plannedBillable:number,plannedNotBillable:number, perCustomer: Map}>} **/
        let durationMap = new Map();
        let byCustomerMap = new Map();
        let noCustomer = new Customer({id: NO_CUSTOMER_ID, name: 'Internal'});

        for(let num of [1,2,3,4,5,6,7]) {
            const date = luxonStartOfWeek.plus({day:num-1});
            durationMap.set(num, {
                weekDay: {index: num, label: TimeTool.weekdayName(num), date: date.toJSDate()},
                billable: 0,
                notBillable:0,
                plannedBillable:0,
                plannedNotBillable:0,
                perCustomer: new Map()
            });
        }

        for (let entry of entries) {


            if (entry.start.getTime() < report.startOfWeek.getTime()) {
                continue;
            }

            let weekDay = entry.start.getDay()
            if (weekDay === 0) {
                weekDay = 7;
            }


            let customer = noCustomer
            let customerId = noCustomer.id;
            if (entry.task.customer && entry.task.customer.id) {
                customer = entry.task.customer;
                customerId = entry.task.customer.id;
            }

            if (!byCustomerMap.has(customerId)) {
                byCustomerMap.set(customerId, {customer:customer, billable:0, notBillable:0});
            }

            if (!durationMap.get(weekDay).perCustomer.has(customerId)) {
                durationMap.get(weekDay).perCustomer.set(customerId, {
                    customer: customer,
                    billable: 0,
                    notBillable: 0,
                });
            }

            if (!entry.isBillable()) {
                report.notBillable += entry.duration;
                durationMap.get(weekDay).notBillable += entry.duration;
                durationMap.get(weekDay).perCustomer.get(customerId).notBillable += entry.duration;
                byCustomerMap.get(customerId).notBillable += entry.duration;
                continue
            }

            report.billable += entry.duration;
            durationMap.get(weekDay).billable += entry.duration;
            durationMap.get(weekDay).perCustomer.get(customerId).billable += entry.duration;
            byCustomerMap.get(customerId).billable += entry.duration;

        }

        for (let plannedTime of plannedTimes) {

            if (plannedTime.start.getTime() < report.startOfWeek.getTime()) {
                continue;
            }

            let weekDay = plannedTime.end.getDay()
            if (weekDay === 0) {
                weekDay = 7;
            }

            let msDuration = plannedTime.end.getTime() - plannedTime.start.getTime()
            let duration = msDuration !== 0 ? Math.abs(msDuration)/1000 : 0;

            if (plannedTime.customer && plannedTime.customer.id) {
                durationMap.get(weekDay).plannedBillable += duration;
                report.plannedBillable += duration;
                continue;
            }

            durationMap.get(weekDay).plannedNotBillable += duration;
            report.plannedNotBillable += duration;

        }

        const sortedCustomerMap = new Map();
        const sortedCustomerEntries = [];
        let internalCustomerEntry;

        for (let customerId of byCustomerMap.keys()) {
            if (customerId === NO_CUSTOMER_ID) {
                internalCustomerEntry = byCustomerMap.get(customerId);
                continue;
            }
            sortedCustomerEntries.push(byCustomerMap.get(customerId));
        }

        sortedCustomerEntries.sort( (a,b) => a.billable > b.billable ? -1 : 1);

        for(let entry of sortedCustomerEntries) {
            sortedCustomerMap.set(entry.customer.id, entry);
        }

        if (internalCustomerEntry) {
            sortedCustomerMap.set(NO_CUSTOMER_ID, internalCustomerEntry);
        }

        report.durationMap = durationMap;
        report.byCustomerMap = sortedCustomerMap;
        return report;

    }

    /**
     * @param {DailyTimesheetReport} report
     * @param {TimesheetEntry[]} entries
     * @param {PlannedTime[]} plannedTimes
     * @return {void}
     * @private
     */
    _addDailyStats(report, entries, plannedTimes) {

        // Also read logged entries from start of month!
        /** @type {Map<string,{billable:number,notBillable:number, plannedBillable:number,plannedNotBillable:number}>} **/
        let durationMap = new Map();

        for (let entry of report.todayEntries) {
            let id = NO_CUSTOMER_ID;
            if (entry.task.customer && entry.task.customer.id) {
                id = entry.task.customer.id;
            }
            if (!durationMap.has(id)) {
                durationMap.set(id, {
                    billable: 0,
                    notBillable: 0,
                    plannedBillable: 0,
                    plannedNotBillable: 0,
                });
            }
            if (!entry.isBillable()) {
                report.notBillable += entry.duration;
                durationMap.get(id).notBillable += entry.duration;
                continue
            }

            report.billable += entry.duration;
            durationMap.get(id).billable += entry.duration;

        }

        for (let plannedTime of report.todayPlannedEntries) {

            let msDuration = plannedTime.end.getTime() - plannedTime.start.getTime()
            let duration = msDuration !== 0 ? Math.abs(msDuration)/1000 : 0;

            let id = NO_CUSTOMER_ID;
            if (plannedTime.customer && plannedTime.customer.id) {
                id = plannedTime.customer.id;
            }

            if (!durationMap.has(id)) {
                durationMap.set(id, {
                    billable: 0,
                    notBillable: 0,
                    plannedBillable: 0,
                    plannedNotBillable: 0,
                });
            }
            if (id === NO_CUSTOMER_ID) {
                durationMap.get(id).plannedNotBillable = duration;
                report.plannedNotBillable += duration;
                continue;
            }
            durationMap.get(id).plannedBillable = duration;
            report.plannedBillable += duration;

        }

        for (let entry of report.previousEntriesFromThisWeek) {
            if (entry.isBillable()) {
                report.weeklyLoggedBillableSecondsBeforeToday += entry.duration;
            }
        }

        for (let entry of report.previousEntriesFromThisMonth) {
            if (entry.isBillable()) {
                report.monthlyLoggedBillableSecondsBeforeToday += entry.duration;
            }
        }

        for (let plannedTime of report.previousPlannedTimesFromThisWeek) {
            if (!plannedTime.customer || !plannedTime.customer.id) {
                continue;
            }
            let msDuration = plannedTime.end.getTime() - plannedTime.start.getTime()
            let duration = msDuration !== 0 ? Math.abs(msDuration)/1000 : 0;
            report.weeklyPlannedBillableSecondsBeforeToday += duration;
        }

        for (let plannedTime of report.previousPlannedTimesFromThisMonth) {
            if (!plannedTime.customer || !plannedTime.customer.id) {
                continue;
            }
            let msDuration = plannedTime.end.getTime() - plannedTime.start.getTime()
            let duration = msDuration !== 0 ? Math.abs(msDuration)/1000 : 0;
            report.monthlyPlannedBillableSecondsBeforeToday += duration;
        }

    }
}